import opentype from 'opentype.js';

// These fonts are broadly available in operational systems, so we don't have files for these.
const SYSTEM_FONTS = new Set(['arial', 'georgia', 'timesnewroman']);

const FONT_FILES = {
  breeserifregular: 'breeserif-regular-webfont',
  caviar_dreamsregular: 'caviardreams-webfont',
  caviar_dreamsbold: 'caviar_dreams_bold-webfont',
  caviar_dreamsitalic: 'caviardreams_italic-webfont',
  caviar_dreamsbold_italic: 'caviardreams_bolditalic-webfont',
  comfortaaregular: 'comfortaa-regular-webfont',
  comfortaabold: 'comfortaa-bold-webfont',
  francois_oneregular: 'francoisone-webfont', // supports fontWeight and fontStyle
  grandhotelregular: 'grandhotel-regular-webfont', // supports fontWeight and fontStyle
  merriweatherregular: 'merriweather-regular-webfont',
  merriweatherbold: 'merriweather-bold-webfont',
  merriweatheritalic: 'merriweather-italic-webfont',
  merriweatherbold_italic: 'merriweather-bolditalic-webfont',
  misoregular: 'miso-regular-webfont',
  misobold: 'miso-bold-webfont',
  museo_slab500: 'museo_slab_500_2-webfont',
  nunitoregular: 'nunito-regular-webfont',
  nunitobold: 'nunito-bold-webfont',
  oleo_scriptregular: 'oleoscript-regular-webfont',
  oleoscriptregular: 'oleoscript-regular-webfont',
  oleo_scriptbold: 'oleoscript-bold-webfont',
  oleoscriptbold: 'oleoscript-bold-webfont',
  roboto_slabregular: 'robotoslab-regular-webfont',
  roboto_slabbold: 'robotoslab-bold-webfont',
  signikaregular: 'signika-regular-webfont',
  signikabold: 'signika-bold-webfont',
  questrialregular: 'questrial-regular-webfont',
  open_sansregular: 'opensans-regular-webfont',
  open_sansitalic: 'opensans-italic-webfont',
  open_sansbold: 'opensans-bold-webfont',
  open_sansbold_italic: 'opensans-bolditalic-webfont',
};

const VARIABLE_FONTS = new Set(['francois_oneregular', 'grandhotelregular', 'questrialregular']);
const BOLD_FONTS = new Set([
  'caviar_dreamsbold',
  'caviar_dreamsbold_italic',
  'comfortaabold',
  'merriweatherbold',
  'merriweatherbold_italic',
  'misobold',
  'nunitobold',
  'oleo_scriptbold',
  'oleoscriptbold',
  'roboto_slabbold',
  'signikabold',
  'open_sansbold',
  'open_sansbold_italic',
]);
const ITALIC_FONTS = new Set([
  'caviar_dreamsitalic',
  'caviar_dreamsbold_italic',
  'merriweatheritalic',
  'merriweatherbold_italic',
  'open_sansitalic',
  'open_sansbold_italic',
]);

class ToSvg {
  fontCache = new Map();
  constructor(textObject, bounds) {
    this.textObject = textObject;
    this.bounds = bounds;
  }

  /**
   * This function loads a font and parses it using opentype.js which gives us multiple utilities to work with fonts.
   * Fonts are also cached
   * @param {string} fontFamily
   * @returns opentype.Font
   */
  async loadFont(fontFamily) {
    if (this.fontCache.has(fontFamily)) {
      return this.fontCache.get(fontFamily);
    }

    const fontFile = FONT_FILES[fontFamily];

    try {
      const fontResponse = await fetch(`/fonts/${fontFile}.ttf`);
      const fontBuffer = await fontResponse.arrayBuffer();
      const font = opentype.parse(fontBuffer);
      this.fontCache.set(fontFamily, font);
      return font;
    } catch (error) {
      console.error(`Error loading font ${fontFile}:`, error);
      throw error;
    }
  }

  async run() {
    const { fontSize, lineHeight = 1.16, text, textLines, styles } = this.textObject.object;

    const fontSizeMult = this.textObject.object._fontSizeMult || 1.13;
    const fontSizeFraction = this.textObject.object._fontSizeFraction || 0.222;

    const rawTextLines = textLines.map((line) => {
      return typeof line === 'string' ? line : line.text || line;
    });
    const mappedStyles = await this.createMappedStyles(styles, text, rawTextLines);

    // arrays to hold all background rects and text elements for all lines
    const backgroundElements = [];
    // array to hold all text elements for all lines
    const textElements = [];
    // array to hold all path elements for all lines
    const pathElements = [];
    let yOffset = 0;
    // go through each line and create svg elements
    for (const [index, textLine] of rawTextLines.entries()) {
      const style = mappedStyles?.[index] ?? {};
      // create ranges of characters with same style
      const ranges = ToSvg.createRanges(style);

      // total width of line after applying styles, needed to calculate horizontal offset based on textAlign
      let fullTextWidth = 0;
      // in case any range has fontSize greater than default fontSize, we need to adjust height of line accordingly
      // height of line affects background rect height(if you have added background to substring) and yOffset for next line
      let maxFontSize = 0;

      // arrays to hold background data for each line
      let lineBackgrounds = [];
      // array to hold text data for each line
      let lineTexts = [];
      let xOffset = 0;
      let prevEnd = 0;
      // go through each range and create svg elements for each substring
      for (const range of ranges) {
        // Add unstyled text before this range if any
        if (range.start > prevEnd) {
          const textLineSubstring = textLine.substring(prevEnd, range.start);
          const width = await this.getSubstringWidth(textLineSubstring, {});
          lineTexts.push({
            text: textLineSubstring,
            style: {},
            x: xOffset,
          });
          xOffset += width;
          fullTextWidth += width;
        }

        // Check for max font size in styled ranges and also create svg for styled text
        if (range.style?.fontSize) {
          if (Number(range.style.fontSize) > Number(maxFontSize)) {
            maxFontSize = Number(range.style.fontSize);
          }
        } else {
          if (Number(fontSize) > Number(maxFontSize)) {
            maxFontSize = fontSize;
          }
        }
        const textLineSubstring = textLine.substring(range.start, range.end);
        const width = await this.getSubstringWidth(textLineSubstring, range.style ?? {});
        lineTexts.push({
          text: textLineSubstring,
          style: range.style ?? {},
          x: xOffset,
        });
        if (range.style?.textBackgroundColor) {
          lineBackgrounds.push({
            x: xOffset,
            width,
            fill: range.style.textBackgroundColor,
          });
        }

        xOffset += width;
        fullTextWidth += width;
        prevEnd = range.end;
      }
      // Add any remaining unstyled text
      if (prevEnd < textLine.length) {
        if (Number(fontSize) > Number(maxFontSize)) {
          maxFontSize = fontSize;
        }
        const textLineSubstring = textLine.substring(prevEnd);
        const width = await this.getSubstringWidth(textLineSubstring, {});
        lineTexts.push({
          text: textLineSubstring,
          style: {},
          x: xOffset,
        });
        xOffset += width;
        fullTextWidth += width;
      }
      let lineHeightPx = maxFontSize * fontSizeMult * lineHeight;
      try {
        lineHeightPx = this.textObject.object.getHeightOfLine(index);
      } catch (e) {
        window.faro?.api?.pushError(e, {
          type: 'fabricjs-text-getHeightOfLine',
          context: { error: e.message },
        });
      }
      const textHeightPx = lineHeightPx / lineHeight;

      const textLineStart = this.getTextLineStart(fullTextWidth);
      for (const { x, width, fill } of lineBackgrounds) {
        backgroundElements.push(
          `<rect x="${textLineStart + x}" y="${yOffset}" width="${width}" height="${textHeightPx}" fill="${fill}"/>`,
        );
      }
      // calculate y position for the text line, by default y position is fonts baseline, so we need to add ascent to yOffset
      const ascent = textHeightPx * (1 - fontSizeFraction);
      for (const { text, style, x } of lineTexts) {
        const fontFamily = style.fontFamily ?? this.textObject.object.fontFamily;
        if (this.useText(fontFamily)) {
          const tspan = await this.getSubstringTspan(
            text,
            style,
            textLineStart + x,
            yOffset + ascent,
          );
          textElements.push(tspan);
        } else {
          const path = await this.getSubstringPath(
            text,
            style,
            textLineStart + x,
            yOffset + ascent,
          );
          pathElements.push(path);
        }
      }
      yOffset += lineHeightPx;
    }

    const innerContent = this.createInnerContent(backgroundElements, textElements, pathElements);

    return {
      content: this.createContent(innerContent),
      defs: '',
      zIndex: this.textObject.zIndex,
    };
  }

  /**
   * Creates a mapping from word-wrapped lines back to original character positions
   * Required to map styles from original text to wrapped text
   * @param {*} originalText - full text
   * @param {*} wrappedLines - array of lines after word-wrapping
   * @returns * - mapping where keys are line indices and values are objects where keys are character indices and values are original text character indices
   */
  static createWordWrapMapping(originalText, wrappedLines) {
    const mapping = [];
    let originalIndex = 0;

    for (let lineIndex = 0; lineIndex < wrappedLines.length; lineIndex++) {
      const lineText =
        typeof wrappedLines[lineIndex] === 'string'
          ? wrappedLines[lineIndex]
          : wrappedLines[lineIndex].text || wrappedLines[lineIndex];

      const lineMapping = {};

      for (let charIndex = 0; charIndex < lineText.length; charIndex++) {
        const currentChar = lineText[charIndex];

        while (originalIndex < originalText.length) {
          if (originalText[originalIndex] === currentChar) {
            lineMapping[charIndex] = originalIndex;
            originalIndex++;
            break;
          } else if (originalText[originalIndex] === '\n' || originalText[originalIndex] === ' ') {
            originalIndex++;
          } else {
            originalIndex++;
          }
        }
      }

      mapping[lineIndex] = lineMapping;
    }

    return mapping;
  }

  /**
   * Creates mapped styles for each line and character after word-wrapping
   * @param {*} originalStyles - styles object where keys are line indices and values are objects where keys are character indices and values are style objects
   * @param {*} originalText - full text
   * @param {*} originalTextLines - array of lines after word-wrapping
   * @returns * - mapped styles where keys are line indices and values are objects where keys are character indices and values are style objects
   */
  async createMappedStyles(originalStyles, originalText, originalTextLines) {
    if (!originalStyles || !originalText || !originalText.length) return null;
    const mapping = ToSvg.createWordWrapMapping(originalText, originalTextLines);
    if (!originalStyles || !mapping) return null;

    const mappedStyles = [];

    let flatStyles = {};

    let counter = 0;
    // originalTextLines have wrapped lines, so we need to split originalText by \n to get unwrapped lines
    const unwrappedTextLines = originalText.split('\n');
    for (let lineIndex = 0; lineIndex < unwrappedTextLines.length; lineIndex++) {
      const lineText = unwrappedTextLines[lineIndex];
      for (let charIndex = 0; charIndex < lineText.length; charIndex++) {
        if (originalStyles && originalStyles[lineIndex] && originalStyles[lineIndex][charIndex]) {
          flatStyles[counter] = originalStyles[lineIndex][charIndex];
        }
        const char = lineText[charIndex];
        const fontFamily =
          originalStyles?.[lineIndex]?.[charIndex]?.fontFamily ?? this.textObject.object.fontFamily;
        if (!this.useText(fontFamily)) {
          const font = await this.loadFont(fontFamily);
          const glyph = font.charToGlyph(char);
          if (glyph.name === '.notdef') {
            flatStyles[counter] = { ...(flatStyles[counter] ?? {}), fontFamily: 'timesnewroman' };
          }
        }
        counter++;
      }
      counter++;
    }

    for (let lineIndex = 0; lineIndex < mapping.length; lineIndex++) {
      const lineMapping = mapping[lineIndex];
      const lineMappedStyles = {};

      Object.keys(lineMapping).forEach((wrappedCharIndex) => {
        const originalCharIndex = lineMapping[wrappedCharIndex];
        if (flatStyles[originalCharIndex]) {
          lineMappedStyles[wrappedCharIndex] = flatStyles[originalCharIndex];
        }
      });

      mappedStyles[lineIndex] = lineMappedStyles;
    }

    return mappedStyles;
  }

  /**
   * Creates ranges of characters with the same style for a given line
   * @param {*} style - style object for a line where keys are character indices and values are style objects
   * @returns Array<{start: number, end: number, style: object}> - list of ranges with same style where start and end are character indices
   */
  static createRanges(style) {
    const styleIndices = Object.keys(style)
      .map(Number)
      .sort((a, b) => a - b);
    const ranges = [];
    if (!styleIndices.length) return ranges;
    let currentRange = {
      start: styleIndices[0],
      end: styleIndices[0] + 1,
      style: style[styleIndices[0]],
    };

    for (let i = 1; i < styleIndices.length; i++) {
      const index = styleIndices[i];
      const prevIndex = styleIndices[i - 1];

      // Check if consecutive and same style
      if (
        index === prevIndex + 1 &&
        JSON.stringify(style[index]) === JSON.stringify(style[prevIndex])
      ) {
        currentRange.end = index + 1;
      } else {
        // Push current range and start a new one
        ranges.push(currentRange);
        currentRange = {
          start: index,
          end: index + 1,
          style: style[index],
        };
      }
    }
    // Add the last range
    ranges.push(currentRange);
    return ranges;
  }

  /**
   *
   * @param {number} fullTextWidth - full width of a text line
   * @returns number - horizontal offset for the text line (changes depending on text align (left, right, center))
   */
  getTextLineStart(fullTextWidth) {
    const { textAlign, width } = this.textObject.object;
    switch (textAlign) {
      case 'center':
        return (width - fullTextWidth) / 2;
      case 'right':
        return width - fullTextWidth;
      default:
        return 0;
    }
  }

  /**
   * This function calculates width of a substring of a text line
   * @param {string} text - substring of line
   * @param {*} style - object consisting of styles for substring
   * @returns {Promise<number>} - width of substring
   */
  async getSubstringWidth(text, style) {
    const fontFamily = style.fontFamily ?? this.textObject.object.fontFamily;
    if (this.useText(fontFamily)) {
      // for system fonts, using tspan element to represent text
      const { fontFamily, fontSize, fontWeight } = this.textObject.object;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.font = `${style.fontWeight ?? fontWeight} ${style.fontSize ?? fontSize}px ${style.fontFamily ?? fontFamily}`;
      return ctx.measureText(text).width;
    } else {
      // using custom style if provided, else fallback to default style
      const fontFamily = style.fontFamily ?? this.textObject.object.fontFamily;
      const fontSize = style.fontSize ?? this.textObject.object.fontSize;

      // for custom fonts, load the font and get path data using opentype.js
      const font = await this.loadFont(fontFamily);
      return font.getAdvanceWidth(text, fontSize);
    }
  }

  /**
   * This function creates tspan for a substring of a text line
   * @param {string} text - substring of line
   * @param {*} style - object consisting of styles for substring
   * @param {number} xOffset - horizontal offset
   * @param {number} yOffset - vertical offset
   * @returns {Promise<string>} - tspan representing substring
   */
  async getSubstringTspan(text, style, xOffset, yOffset) {
    // apply various styles but dont fallback to default styles as they are applied to parent text element
    return `<tspan
      x="${xOffset}"
      y="${yOffset}"
      ${style.fill ? `fill="${style.fill}"` : ''}
      ${style.fontWeight ? `font-weight="${style.fontWeight}"` : ''}
      ${style.fontStyle ? `font-style="${style.fontStyle}"` : ''}
      ${style.fontSize ? `font-size="${style.fontSize}"` : ''}
      ${style.fontFamily ? `font-family="${style.fontFamily}"` : ''}
      ${style.underline ? `text-decoration="underline"` : ''}
      ${style.deltaY ? `baseline-shift="${-1 * style.deltaY}"` : ''}
    >${ToSvg.sanitize(text)}</tspan>`;
  }

  /**
   * This function creates path for a substring of a text line
   * @param {string} text - substring of line
   * @param {*} style - object consisting of styles for substring
   * @param {number} xOffset - horizontal offset
   * @param {number} yOffset - vertical offset
   * @returns {Promise<string>} - path representing substring
   */
  async getSubstringPath(text, style, xOffset, yOffset) {
    // using custom style if provided, else fallback to default style
    const fontFamily = style.fontFamily ?? this.textObject.object.fontFamily;
    const fontSize = style.fontSize ?? this.textObject.object.fontSize;
    const fontWeight = style.fontWeight ?? this.textObject.object.fontWeight;
    const fontStyle = style.fontStyle ?? this.textObject.object.fontStyle;
    const fill = style.fill || this.textObject.object.fill || '#000000';
    const stroke = style.stroke ?? this.textObject.object.stroke;
    const strokeWidth = style.strokeWidth ?? this.textObject.object.strokeWidth;

    // for custom fonts, load the font and get path data using opentype.js
    const font = await this.loadFont(fontFamily);
    const textWidth = font.getAdvanceWidth(text, fontSize);
    const pathData = font.getPath(text, xOffset, yOffset, fontSize).toPathData();

    // apply various styles
    const isBold = BOLD_FONTS.has(fontFamily);
    const isItalic = ITALIC_FONTS.has(fontFamily);
    let strokeAttributes = '';
    if (fontWeight === 'bold' && !isBold) {
      strokeAttributes = `stroke="${fill}" stroke-width="${fontSize * 0.04}"`;
      if (stroke && strokeWidth != null) {
        strokeAttributes = ` stroke="${stroke}" stroke-width="${strokeWidth + fontSize * 0.04}"`;
      }
    } else {
      if (stroke && strokeWidth != null) {
        strokeAttributes = `stroke="${stroke}" stroke-width="${strokeWidth}"`;
      }
    }
    let path = `<path
      d="${pathData}"
      ${fill ? `fill="${fill}"` : ''}
      ${strokeAttributes}
      ${fontStyle === 'italic' && !isItalic ? `transform="translate(0, ${yOffset}) skewX(-12) translate(0, -${yOffset})"` : ''}
      ${style.deltaY ? `transform="translate(0, ${style.deltaY})"` : ''}
    />`;
    if (style.underline) {
      const underlineY = fontSize * 0.1;
      const underlineThickness = fontSize * 0.08;
      path += `<rect x="${xOffset}" y="${yOffset + (style.deltaY || 0) + underlineY}" width="${textWidth}" height="${underlineThickness}" fill="${fill}"/>`;
    }

    return path;
  }

  /**
   * This function creates the inner content by combining backgroundElements and textElements
   * This function needs to be implemented in subclasses as they have different implementations based on whether system fonts or custom fonts are used
   * @param {string[]} backgroundElements - list of rect elements
   * @param {string[]} textElements - list of text related elements: tspan
   * @param {string[]} pathElements - list of text related elements: path
   * @returns string - innerContent consisting of backgroundElements and textElements
   *
   */
  createInnerContent(backgroundElements, textElements, pathElements) {
    const { fontFamily, fontSize, fontWeight, fontStyle, fill, stroke, strokeWidth } =
      this.textObject.object;
    // for custom fonts, we use g element to group backgroundElements(rect elements) and text element to group textElements(tspan elements)
    // applying default styles to text element and custom styles will be applied to tspans present in textElements
    return `
      ${backgroundElements.length ? `<g>${backgroundElements.join('')}</g>` : ''}
      ${
        textElements.length
          ? `<text 
              font-family="${fontFamily}"
              font-size="${fontSize}"
              ${fontWeight !== 'normal' ? `font-weight="${fontWeight}"` : ''}
              ${fontStyle !== 'normal' ? `font-style="${fontStyle}"` : ''}
              fill="${fill || 'none'}"
              ${stroke ? `stroke="${stroke}"` : ''}
              ${strokeWidth != null ? `stroke-width="${strokeWidth}"` : ''}
              xml:space="preserve"
            >${textElements.join('')}</text>`
          : ''
      }
      ${pathElements.length ? `<g>${pathElements.join('')}</g>` : ''}
    `;
  }

  /**
   * This function creates the final parent svg group element for all text lines
   * This includes applying parent level transforms, opacity, background color and shadow
   * @param {string} innerContent - svg elements like g, text, tspan, rect
   * @returns string - complete svg group element with transforms and styles applied
   */
  createContent(innerContent) {
    const {
      left,
      top,
      angle,
      scaleX,
      scaleY,
      flipX = false,
      flipY = false,
      height,
      width,
      opacity = 1,
      backgroundColor,
      shadow,
    } = this.textObject.object;

    const fabricLeft = left - this.bounds.actualLeft;
    const fabricTop = top - this.bounds.actualTop;
    const adjustedLeft = flipX ? fabricLeft + width : fabricLeft;
    const adjustedTop = flipY ? fabricTop + height : fabricTop;
    const transforms = [
      `translate(${adjustedLeft}, ${adjustedTop})`,
      angle ? `rotate(${angle})` : null,
      flipX || flipY ? `scale(${flipX ? -1 : 1}, ${flipY ? -1 : 1})` : null,
      scaleX !== 1 || scaleY !== 1 ? `scale(${scaleX}, ${scaleY})` : null,
    ].filter(Boolean);

    return `
      ${shadow ? `<filter id="shadow"><feDropShadow dx="-2" dy="2" stdDeviation="1" flood-opacity="0.4"/></filter>` : ''}
      <g
        transform="${transforms.join(' ')}"
        style="opacity: ${opacity}"
        ${shadow ? `filter="url(#shadow)"` : ''}
      >
        ${backgroundColor ? `<rect x="0" y="0" width="${width}" height="${height}" fill="${backgroundColor}" />` : ''}
        ${innerContent}
      </g>
    `;
  }

  static sanitize(text) {
    if (!text) return '';

    return text
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/g, '')
      .replace(/[&<>"']/g, (char) => {
        const entities = {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&apos;',
        };

        return entities[char];
      });
  }

  useText(fontFamily) {
    return SYSTEM_FONTS.has(fontFamily) || !FONT_FILES[fontFamily] || this.preserveTexts;
  }
}

class TextHandler {
  constructor(canvas, bounds, preserveTexts = false) {
    this.canvas = canvas;
    this.bounds = bounds;
    this.preserveTexts = preserveTexts;
    this.fontCache = new Map();
  }

  async processTextObject(textObject) {
    const toSvg = new ToSvg(textObject, this.bounds);
    return toSvg.run();
  }

  async extractTextElements(texts) {
    if (!texts || texts.length === 0) {
      return [];
    }

    try {
      const processedTexts = await Promise.all(texts.map((text) => this.processTextObject(text)));

      return processedTexts.filter(Boolean);
    } catch (error) {
      console.error('Error processing texts:', error);
      throw error;
    }
  }
}

export default TextHandler;
